import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import styles from '@/interface/ui/_shared/items/calenderButtonGroup.module.scss';
import iCal from '@/interface/ui/_shared/calender/iCal';
import googleCal from '@/interface/ui/_shared/calender/googleCal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Launch from '@/domain/launch';
import colors from '@/constants/colors';
import type Event from '@/domain/events/index';

type Props = {
  item: Event | Launch;
  userAgent?: string;
  calTitle?: string;
  buttonStyle?: string;
  position?: 'left' | 'right';
};

const CalenderButtonGroup = (
  { item, userAgent, calTitle, buttonStyle, position }: Props,
  ref
) => {
  const userAgents: string = userAgent || '';
  const title: string = item.calendarTitle || item.title;
  const startDate: Date = item.calendarStartAt || new Date(item.releaseAt);
  const endDate: Date = item.calendarEndAt || getEndDate(startDate);
  const [showPopOver, setShowPopOver] = useState(false);
  const [isHoverIcal, setHoverIcal] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const documentClickHandler = useRef<(e: any) => void>(() => {});

  const removeDocumentClickHandler = useCallback(() => {
    document.removeEventListener('click', documentClickHandler.current);
  }, []);

  const togglePopOver = useCallback(() => {
    setShowPopOver(!showPopOver);

    if (!showPopOver) {
      // 100ms後
      setTimeout(() => {
        document.addEventListener('click', documentClickHandler.current);
      }, 100);
    }
  }, [showPopOver]);

  const hoverIcal = () => {
    setHoverIcal(true);
  };

  const removeHover = () => {
    setHoverIcal(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      togglePopOver,
    }),
    [togglePopOver]
  );

  useEffect(() => {
    documentClickHandler.current = (e) => {
      if (wrapperRef?.current?.contains(e.target)) return;

      setShowPopOver(false);
      removeDocumentClickHandler();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderPopOver = useCallback(() => {
    const customStyle = {
      left:
        position === 'left' ? '-120px' : position === 'right' ? '90px' : '0px',
    } as const;

    return (
      <div
        className={
          isHoverIcal ? styles.popover__contents : styles.popover__content
        }
        style={customStyle}
      >
        {
          (isIOSorMacOS(userAgents)
            ? iCal(title, startDate, endDate, hoverIcal, removeHover)
            : null) as React.ReactNode
        }
        {googleCal(title, startDate, endDate) as React.ReactNode}
      </div>
    );
  }, [showPopOver, userAgents]);

  return (
    <div className={styles.popover__wrapper} onClick={togglePopOver}>
      {calTitle ? (
        <p className={styles.button}>{calTitle}</p>
      ) : (
        <FontAwesomeIcon
          icon="calendar-alt"
          color={colors.lightGray}
          size="lg"
          className={buttonStyle || styles.calendarIcon}
        />
      )}
      <div ref={wrapperRef}>{showPopOver && renderPopOver()}</div>
    </div>
  );
};

const isIOSorMacOS = function (userAgent: string): boolean {
  return Boolean(userAgent.match(/iPhone|iPad|iPod|Mac OS/i));
};

function getEndDate(date: Date): Date {
  date.setHours(date.getHours() + 3);
  return date;
}

export default forwardRef(CalenderButtonGroup);
